import React, { FC, memo, useCallback, useMemo, useState } from "react";
import {
  FoodItem,
  FoodItemKey,
  GordonFoodServiceInvoiceRecord,
  IngredientState,
  StorageTemperature,
} from "client/jspPlatformExperiment";
import CommonButton from "components/common/ui/CommonButton";
import SpacedTextField from "components/common/ui/SpacedTextField";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
} from "@mui/material";
import IngredientEditForms from "components/food/ui/ingredient/IngredientEditForms";
import { actions } from "store/slices/foodItem/foodItemDetailSlice";
import { useAppDispatch } from "hooks/useReduxStore";
import dayjs from "dayjs";
import AdaptiveRowHeader from "components/common/ui/AdaptiveRowHeader";
import RelinkFoodActionForm from "components/food/ui/supplierInfo/invoiceUpdateSupplier/RelinkFoodActionForm";
import { factorKG2Uom } from "utils/common/quantityUnitConversion";
import { RoundedItemBox } from "components/common/ui/RoundedItemBox";
import { invoiceUpdateSupplierInfoActions } from "store/slices/foodItem/invoiceUpdateSupplierInfoSlice";
import { SupplierInfoActionStatus } from "models/food/supplierInfo/actionStatus";

export type GFSInvoiceItemActionState = {
  chooseAction?: SupplierInfoActionStatus;
  actionMsg?: string;
};

interface GFSInvoiceItemActionBoxProps {
  displayKey: string;
  data: GordonFoodServiceInvoiceRecord;
  state: GFSInvoiceItemActionState;
  ingredientOptions: FoodItemKey[];
}

const GFSInvoiceItemActionBox: FC<GFSInvoiceItemActionBoxProps> = ({
  displayKey,
  data,
  state,
  ingredientOptions,
}) => {
  const dispatch = useAppDispatch();
  const [openFoodItemEdit, setOpenFoodItemEdit] = useState(false);

  const netWeightOffPercentage = useCallback(
    (GFSData: GordonFoodServiceInvoiceRecord) => {
      if (!GFSData.net_weight) {
        return undefined; // Avoid division by zero
      }
      return Math.abs(
        (GFSData.pack_size * GFSData.unit_size) /
          factorKG2Uom(GFSData.unit_size_uom) /
          GFSData.net_weight -
          1,
      );
    },
    [],
  );

  const isSpecialCase = useCallback(
    (GFSData: GordonFoodServiceInvoiceRecord) => {
      const offPercentageNetWeight = netWeightOffPercentage(GFSData);
      return (
        (offPercentageNetWeight && offPercentageNetWeight > 0.35) ||
        (!offPercentageNetWeight && GFSData.unit_size === GFSData.pack_size)
      );
    },
    [netWeightOffPercentage],
  );

  const handleChooseActionChange = useCallback(
    (newValue: SupplierInfoActionStatus | undefined) => () => {
      dispatch(
        invoiceUpdateSupplierInfoActions.updateAnActionItem({
          key: displayKey,
          newData: {
            ...state,
            chooseAction: newValue,
          },
        }),
      );
      if (newValue === SupplierInfoActionStatus.CREATING) {
        dispatch(
          actions.setFoodItemDetail({
            id: 0,
            food_name: data.product_description,
            state: IngredientState.NONE,
            category: "other",
            storage_temp: StorageTemperature.FRIDGE,
            shelf_life: dayjs.duration({ days: 3 }).toISOString(),
            dispense_category: "",
            food_nutrition_records: [],
            food_suppliers: [
              {
                id: 0,
                food_id: 0,
                supplier_id: "GFS",
                product_code: data.item_code ?? "",
                product_name: data.product_description ?? "",
                pack_size: isSpecialCase(data) ? 1 : data.pack_size, // pack_size
                pack_price: Math.round(
                  (data.total_price / data.quantity_shipped) * 100,
                ), // in cents
                unit_size:
                  (data.unit_size * 1000) / factorKG2Uom(data.unit_size_uom),
                supplier_unit_size: data.unit_size,
                supplier_unit_size_uom: data.unit_size_uom,
                url: `https://order.gfs.com/product/${data.item_code}`,
              },
            ], // Array<FoodItemSupplierInfo>;
          } as FoodItem),
        );
        setOpenFoodItemEdit(true);
      } else {
        setOpenFoodItemEdit(false);
      }
    },
    [data, dispatch, displayKey, isSpecialCase, state],
  );

  const creatNewSuccessCallback = useCallback(() => {
    setOpenFoodItemEdit(false);
    dispatch(
      invoiceUpdateSupplierInfoActions.updateAnActionItem({
        key: displayKey,
        newData: {
          chooseAction: SupplierInfoActionStatus.CREATED,
          actionMsg: "Created a new food item",
        },
      }),
    );
  }, [dispatch, displayKey]);

  const relinkSuccessCallback = useCallback(
    (msg: string) => {
      dispatch(
        invoiceUpdateSupplierInfoActions.updateAnActionItem({
          key: displayKey,
          newData: {
            chooseAction: SupplierInfoActionStatus.RELINKED,
            actionMsg: msg,
          },
        }),
      );
    },
    [dispatch, displayKey],
  );

  const actionForm = useMemo(() => {
    if (state.chooseAction === SupplierInfoActionStatus.RELINKING) {
      return (
        <RelinkFoodActionForm
          newSupplierData={{
            supplier_id: "GFS",
            product_code: data.item_code ?? undefined,
            product_name: data.product_description ?? undefined,
            pack_size: isSpecialCase(data) ? 1 : data.pack_size, // pack_size
            pack_price: Math.round(
              (data.total_price / data.quantity_shipped) * 100,
            ), // in cents
            unit_size:
              (data.unit_size * 1000) / factorKG2Uom(data.unit_size_uom),
            supplier_unit_size: data.unit_size,
            supplier_unit_size_uom: data.unit_size_uom,
            url: !data.item_code
              ? undefined
              : `https://order.gfs.com/product/${data.item_code}`,
          }}
          ingredientOptions={ingredientOptions}
          onClose={handleChooseActionChange(undefined)}
          onRelink={relinkSuccessCallback}
        />
      );
    } else if (
      state.chooseAction === SupplierInfoActionStatus.SKIPPED
      // aaa
      // state.chooseAction === SupplierInfoActionStatus.CREATING ||
      // state.chooseAction === SupplierInfoActionStatus.CREATED ||
      // state.chooseAction === SupplierInfoActionStatus.RELINKED
    ) {
      return (
        <AdaptiveRowHeader switchWidthInPixel={1}>
          <Chip
            label="Skipped"
            onDelete={handleChooseActionChange(undefined)}
          />
        </AdaptiveRowHeader>
      );
    }
    return (
      <AdaptiveRowHeader switchWidthInPixel={1}>
        <Chip label={state.actionMsg} />
      </AdaptiveRowHeader>
    );
  }, [
    state.chooseAction,
    state.actionMsg,
    data,
    isSpecialCase,
    ingredientOptions,
    handleChooseActionChange,
    relinkSuccessCallback,
  ]);

  return (
    <RoundedItemBox>
      <Grid container spacing={1} columns={24}>
        <Grid item xs={24} md={12} lg={11}>
          <SpacedTextField
            label="Product description"
            value={data.product_description}
            multiline
            gray
          />
        </Grid>
        <Grid item xs={9} sm={6} md={3}>
          <SpacedTextField label="Item code" value={data.item_code} gray />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <SpacedTextField
            label="Qty"
            value={data.quantity_shipped}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">{data.price_uom}</InputAdornment>
              ),
            }}
            gray
          />
        </Grid>
        <Grid item xs={9} sm={4} md={4} lg={2}>
          <SpacedTextField label="Units/case" value={data.pack_size} gray />
        </Grid>
        <Grid item xs={8} sm={4} md={3} lg={3}>
          <SpacedTextField
            label="Unit size"
            value={data.unit_size}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {data.unit_size_uom}
                </InputAdornment>
              ),
            }}
            gray
          />
        </Grid>
        <Grid item xs={8} sm={6} md={4} lg={3}>
          <SpacedTextField
            label="Total amount"
            value={
              data.quantity_shipped *
              (isSpecialCase(data) ? 1 : data.pack_size) *
              data.unit_size
            }
            // error={netWeightOffPercentageAdapted(data) > 0.35}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {data.unit_size_uom}
                </InputAdornment>
              ),
            }}
            gray
          />
        </Grid>
        <Grid item xs={8} sm={6} md={4} lg={3}>
          <SpacedTextField
            label="Total price"
            value={Math.round(data.total_price * 100) / 100}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            gray
          />
        </Grid>
        {/* <Grid item xs={24} lg={13}>*/}
        <Grid item xs={24} md={16} lg={21}>
          <Box display="flex" justifyContent="flex-start">
            {state.chooseAction ? (
              actionForm
            ) : (
              <>
                <CommonButton
                  text="Skip"
                  onClick={handleChooseActionChange(
                    SupplierInfoActionStatus.SKIPPED,
                  )}
                  positive
                />
                <CommonButton
                  text="Link to food"
                  onClick={handleChooseActionChange(
                    SupplierInfoActionStatus.RELINKING,
                  )}
                  positive
                />
                <CommonButton
                  text="Create new food"
                  onClick={handleChooseActionChange(
                    SupplierInfoActionStatus.CREATING,
                  )}
                  positive
                />
              </>
            )}
          </Box>
        </Grid>
      </Grid>
      <Dialog
        fullScreen
        open={openFoodItemEdit}
        onClose={handleChooseActionChange(undefined)}
        scroll="paper"
      >
        <DialogTitle>Create New Food</DialogTitle>
        <DialogContent dividers>
          <IngredientEditForms saveButtonCallback={creatNewSuccessCallback} />
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={handleChooseActionChange(undefined)}
            text="Cancel"
          />
        </DialogActions>
      </Dialog>
    </RoundedItemBox>
  );
};

export default memo(GFSInvoiceItemActionBox);
